<script setup>
import {reactive, onMounted, ref} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const data = reactive({
  editedRuleItem: {
    person: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    role: {
      id: null,
      role: ''
    },
    isNew: true
  },
  // editedRuleItem: {},
  isEditMode: false,
  clients: [],
  loading: false,
  loadingText: "Loading rules...Please wait",
  messageNoData: 'No autosharing rules currently available.',
  people: [],
  roles: [],
  rules: [],
  rulesHeaders: [
    {
      title: 'Person',
      align: 'start',
      key: 'personname',
      groupable: false,
    },
    {
      title: 'Client',
      align: 'start',
      key: 'clientname',
      groupable: true,
    },
    {
      title: 'Role',
      align: 'start',
      key: 'rolename',
      groupable: false,
    },
    {
      title: 'Actions',
      key: 'actions',
      groupable: false,
      sortable: false
    },
  ],
  search: '',
  validRuleForm: false
});

const editRuleForm = ref(null);

const createNewRuleItemObject = () => {
  data.editedRuleItem = {
    person: {},
    client: {},
    role: {},
    isNew: true
  }
}

const discardRule = () => {
  createNewRuleItemObject()
  data.isEditMode = false
  getData()
}

const deleteRule = (rule) => {
  data.rules.splice(data.rules.indexOf(rule))
  data.loading = true
  ApiWrapper.post('auto-sharing','delete-rule', rule)
  .then(result => {
    getData()

    ToastUtil.successMsg('Deleted auto-sharing rule');
  })
  .catch(e => {
    ToastUtil.error(e);
    data.loading = false;
  })
}

const editRule = (rule) => {
  // data.rules.splice(data.rules.indexOf(rule))
  // data.editedRuleItem = {
  //   rule: rule.ruleid,
  //   person: rule.personid,
  //   client: rule.clientid,
  //   role: rule.roleid
  // }
  data.editedRuleItem = {
    person: {
      id: rule.personid,
      name: rule.personname
    },
    client: {
      id: rule.clientid,
      name: rule.clientname
    },
    role: {
      id: rule.roleid,
      role: rule.rolename
    },
    rule: {
      id: rule.ruleid
    },
    isNew: false
  }
  data.isEditMode = true;
}

const filterBySearch = (value, search, item) => {
  return  value != null &&
      search != null &&
      typeof value === 'string' &&
      value.toString().toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1
}

const getData = () => {

  data.loading = true
  ApiWrapper.post('auto-sharing','list-rules',{})
  .then(response => {
    data.clients = response.clients
    data.people  = response.people
    data.roles   = response.roles
    data.rules   = response.rules
  })
  .catch(e => {
    ToastUtil.error(e);
    data.messageNoData = 'Failed to load existing rules. Try again later.'
  })
  .finally(() => {
    data.loading = false
  })
}

const saveRule = (item) => {
  if (editRuleForm.value.validate()) {
    data.loading = true
    let logicFn = item.isNew ? 'create-rule' : 'edit-rule';
    let rule = {
      person: item.person.name,
      client: item.client.name,
      role: item.role.role
    }
    if(item.rule){
      if(item.rule.id){
        rule.rule = item.rule.id;
      }
    }
    ApiWrapper.post('auto-sharing',logicFn, rule)
    .then(response => {
      discardRule()
      getData()

      ToastUtil.successMsg(item.isNew ? 'Created auto-sharing rule' : 'Updated auto-sharing rule');
    })
    .catch((err) => {
      ToastUtil.error(err);
      data.loading = false;
      data.isEditMode = false;
    })
  }
}

onMounted(()=>{
  getData()
})
</script>

<template>
  <v-container fluid>
    <v-toolbar density="compact" flat color="white">
      <v-toolbar-title>Autosharing Rules</v-toolbar-title>
      <v-divider class="mx-4" inset vertical></v-divider>
      <v-spacer></v-spacer>
      <v-btn v-if="Permissions.checkPermissions(['autosharing.create'])" size="small" variant="elevated" color="primary" dark class="mb-2" @click="createNewRuleItemObject(); data.isEditMode = true">New Rule</v-btn>
    </v-toolbar>
    <v-form id="editRuleForm" v-if="data.isEditMode" ref="editRuleForm" v-model="data.validRuleForm" lazy-validation>
      <v-row class="px-5">
        <v-col cols="12" sm="3">
          <v-autocomplete
              class="px-3"
              v-model="data.editedRuleItem.person.name"
              :items="data.people"
              :rules="[v => !!v || 'Person is required', v => Number.isInteger(parseInt(v)) || 'Person is required']"
              label="Person"
              clearable
              item-title="name"
              item-value="id"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="3">
          <v-autocomplete
              class="px-3"
              v-model="data.editedRuleItem.client.name"
              :items="data.clients"
              :rules="[v => !!v || 'Client is required', v => Number.isInteger(parseInt(v)) || 'Client is required']"
              label="Client"
              clearable
              item-title="name"
              item-value="id"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="3">
          <v-autocomplete
              class="px-3"
              v-model="data.editedRuleItem.role.role"
              :items="data.roles"
              :rules="[v => !!v || 'Role is required', v => Number.isInteger(parseInt(v)) || 'Role is required']"
              label="Role"
              clearable
              item-title="role"
              item-value="id"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="3">
          <v-btn size="small" variant="elevated" class="px-2" @click="saveRule(data.editedRuleItem)" color="primary" :disabled="!data.validRuleForm">
            <v-icon color="white">mdi-plus</v-icon>
          </v-btn>
          <v-btn size="small" variant="elevated" class="px-2" @click="discardRule()" color="error">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-data-table
        :headers="data.rulesHeaders"
        :items="data.rules"
        item-value="ruleid"
        :loading="data.loading"
        :loading-text="data.loadingText"
        :custom-filter="filterBySearch"
        :search="data.search"
        class="elevation-1"
        show-group-by
    >
      <template v-slot:top>
        <v-text-field
            v-model="data.search"
            label="Search"
            class="mx-4"
        ></v-text-field>
      </template>
      <template v-slot:no-data>
        {{ data.messageNoData }}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-icon v-if="Permissions.checkPermissions(['autosharing.update'])" @click="editRule(item)">mdi-pencil </v-icon>
<!--        <v-icon @click="">mdi-pencil </v-icon>-->
        <v-icon v-if="Permissions.checkPermissions(['autosharing.delete'])" @click="deleteRule(item)">mdi-delete </v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<style scoped>
.container {
  padding: 5px;
}
</style>